import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { useNavigate } from "react-router-dom"
import {
  PostUserapiResultsSimulationStatusApiResponse,
  usePostUserapiResultsSimulationStatusMutation,
} from "../../../app/services/meseeknaApi"
import { Role, User } from "../types"

export type SimulationStatus = "not-started" | "in-progress" | "completed"

type SimulationStatusResponse = {
  status: SimulationStatus
  error?: string
}

type UserSimStatusResponse =
  | { data: PostUserapiResultsSimulationStatusApiResponse }
  | { error: FetchBaseQueryError | SerializedError }

const isSimulationInProgress = (response: UserSimStatusResponse): boolean => {
  return (
    "data" in response &&
    (response?.data?.data?.length ?? 0) > 0 &&
    !(response?.data?.data?.[0]?.dateCompleted ?? "")
  )
}

const isSimulationNotStarted = (response: UserSimStatusResponse): boolean => {
  return "data" in response && (response?.data?.data?.length ?? 0) === 0
}

export const useRoleRedirect = () => {
  const navigate = useNavigate()

  const [userSimStatus] = usePostUserapiResultsSimulationStatusMutation()

  const handleRedirect = (roleId: string) => {
    switch (roleId) {
      case "1":
        navigate("/dashboard/super-admin")
        break
      case "8":
        navigate("/dashboard/recruiter-admin")
        break
      case "3":
        navigate("/dashboard/recruiter")
        break
      case "5":
        navigate("/dashboard")
        break
      default:
        console.error("Unknown role:", roleId)
    }
  }

  const handleRoleRedirectForRole = (role: Role) => {
    const roleId = String(role.roleId)
    handleRedirect(roleId)
  }

  const handleRoleRedirectForUser = (userData: User) => {
    const roleId = String(userData.role)
    handleRedirect(roleId)
  }

  const handleSimulationStatusRedirect = (status: SimulationStatus) => {
    switch (status) {
      case "completed":
        navigate("/dashboard")
        break
      case "in-progress":
      case "not-started":
        navigate("/dashboard")
        break
      default:
        console.error("Unknown simulation status:", status)
    }
  }

  const getSimulationStatus = async (
    testId: number,
    orgId: number,
    userId: number,
  ): Promise<SimulationStatusResponse> => {
    try {
      const simStatusRes = await userSimStatus({
        testId,
        organizationId: orgId,
        userId,
      })

      if (isSimulationInProgress(simStatusRes)) {
        return { status: "in-progress" }
      } else if (isSimulationNotStarted(simStatusRes)) {
        return { status: "not-started" }
      } else {
        return { status: "completed" }
      }
    } catch (error) {
      const errorObject = error as { data?: { message?: string } }
      const errorMessage = errorObject?.data?.message || "Something went wrong"
      return { status: "not-started", error: errorMessage }
    }
  }

  return {
    handleRoleRedirectForRole,
    handleRoleRedirectForUser,
    handleSimulationStatusRedirect,
    getSimulationStatus,
  }
}
